import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiService } from '../services/api.js'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const token = ref(localStorage.getItem('token') || null)
  const loading = ref(false)
  const error = ref(null)

  // Computed properties
  const isAuthenticated = computed(() => !!token.value)
  const userRole = computed(() => user.value?.role || null)
  const isAdmin = computed(() => userRole.value === 'admin')
  const isCustomer = computed(() => userRole.value === 'customer')

  // Actions
  const login = async (credentials) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await apiService.login(credentials)
      const { token: newToken, user: userData } = response
      
      token.value = newToken
      user.value = userData
      
      localStorage.setItem('token', newToken)
      
      return { success: true }
    } catch (err) {
      error.value = err.response?.data?.error || 'Login failed'
      console.error('Login error:', err)
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const register = async (userData) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await apiService.register(userData)
      const { token: newToken, user: userInfo } = response
      
      token.value = newToken
      user.value = userInfo
      
      localStorage.setItem('token', newToken)
      
      return { success: true }
    } catch (err) {
      error.value = err.response?.data?.error || 'Registration failed'
      console.error('Registration error:', err)
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    token.value = null
    user.value = null
    localStorage.removeItem('token')
  }

  const fetchProfile = async () => {
    try {
      if (!token.value) return { success: false }
      
      const response = await apiService.getProfile()
      user.value = response.user
      return { success: true }
    } catch (err) {
      console.error('Fetch profile error:', err)
      if (err.response?.status === 401) {
        logout()
      }
      return { success: false, error: err.message }
    }
  }

  const clearError = () => {
    error.value = null
  }

  const initializeAuth = async () => {
    if (token.value) {
      await fetchProfile()
    }
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    
    // Computed
    isAuthenticated,
    userRole,
    isAdmin,
    isCustomer,
    
    // Actions
    login,
    register,
    logout,
    fetchProfile,
    clearError,
    initializeAuth
  }
}) 